import { create } from 'zustand';

export interface ChatMessage {
  id: string;
  senderId: string;
  senderName: string;
  content: string;
  timestamp: Date;
  isRead: boolean;
}

export interface ChatRoom {
  id: string;
  name: string;
  lastMessage?: ChatMessage;
  unreadCount: number;
  isOnline: boolean;
  avatar?: string;
}

export interface ChatStore {
  isChatOpen: boolean;
  activeRoomId: string | null;
  chatRooms: ChatRoom[];
  messages: Record<string, ChatMessage[]>;
  
  // Actions
  toggleChat: () => void;
  openChat: () => void;
  closeChat: () => void;
  setActiveRoom: (roomId: string | null) => void;
  sendMessage: (roomId: string, content: string) => void;
  markAsRead: (roomId: string) => void;
}

// 샘플 데이터
const sampleRooms: ChatRoom[] = [
  {
    id: 'room1',
    name: '김지민 (BTS)',
    unreadCount: 2,
    isOnline: true,
    lastMessage: {
      id: 'msg1',
      senderId: 'user1',
      senderName: '김지민',
      content: 'Dynamite 리믹스 작업 언제 완료되나요?',
      timestamp: new Date(Date.now() - 1000 * 60 * 5), // 5분 전
      isRead: false
    }
  },
  {
    id: 'room2',
    name: '허윤진 (LE SSERAFIM)',
    unreadCount: 0,
    isOnline: true,
    lastMessage: {
      id: 'msg2',
      senderId: 'user2',
      senderName: '허윤진',
      content: '감사합니다! 작업 확인했습니다.',
      timestamp: new Date(Date.now() - 1000 * 60 * 30), // 30분 전
      isRead: true
    }
  },
  {
    id: 'room3',
    name: '안유진 (IVE)',
    unreadCount: 1,
    isOnline: false,
    lastMessage: {
      id: 'msg3',
      senderId: 'user3',
      senderName: '안유진',
      content: 'XOXZ 프로젝트 관련해서 문의드립니다.',
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2), // 2시간 전
      isRead: false
    }
  }
];

const sampleMessages: Record<string, ChatMessage[]> = {
  room1: [
    {
      id: 'msg1-1',
      senderId: 'me',
      senderName: 'Admin',
      content: '안녕하세요! Dynamite 리믹스 작업을 확인해드리겠습니다.',
      timestamp: new Date(Date.now() - 1000 * 60 * 10),
      isRead: true
    },
    {
      id: 'msg1-2',
      senderId: 'user1',
      senderName: '김지민',
      content: 'Dynamite 리믹스 작업 언제 완료되나요?',
      timestamp: new Date(Date.now() - 1000 * 60 * 5),
      isRead: false
    }
  ],
  room2: [
    {
      id: 'msg2-1',
      senderId: 'me',
      senderName: 'Admin',
      content: '작업이 완료되었습니다. 확인 부탁드립니다.',
      timestamp: new Date(Date.now() - 1000 * 60 * 35),
      isRead: true
    },
    {
      id: 'msg2-2',
      senderId: 'user2',
      senderName: '허윤진',
      content: '감사합니다! 작업 확인했습니다.',
      timestamp: new Date(Date.now() - 1000 * 60 * 30),
      isRead: true
    }
  ],
  room3: [
    {
      id: 'msg3-1',
      senderId: 'user3',
      senderName: '안유진',
      content: 'XOXZ 프로젝트 관련해서 문의드립니다.',
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2),
      isRead: false
    }
  ]
};

export const useChatStore = create<ChatStore>((set) => ({
  isChatOpen: false,
  activeRoomId: null,
  chatRooms: sampleRooms,
  messages: sampleMessages,

  toggleChat: () => set((state) => ({ isChatOpen: !state.isChatOpen })),
  
  openChat: () => set({ isChatOpen: true }),
  
  closeChat: () => set({ isChatOpen: false, activeRoomId: null }),
  
  setActiveRoom: (roomId) => set({ activeRoomId: roomId }),
  
  sendMessage: (roomId, content) => {
    const newMessage: ChatMessage = {
      id: `msg-${Date.now()}`,
      senderId: 'me',
      senderName: 'Admin',
      content,
      timestamp: new Date(),
      isRead: true
    };
    
    set((state) => ({
      messages: {
        ...state.messages,
        [roomId]: [...(state.messages[roomId] || []), newMessage]
      },
      chatRooms: state.chatRooms.map(room => 
        room.id === roomId 
          ? { ...room, lastMessage: newMessage }
          : room
      )
    }));
  },
  
  markAsRead: (roomId) => {
    set((state) => ({
      chatRooms: state.chatRooms.map(room =>
        room.id === roomId
          ? { ...room, unreadCount: 0 }
          : room
      ),
      messages: {
        ...state.messages,
        [roomId]: (state.messages[roomId] || []).map(msg => ({
          ...msg,
          isRead: msg.senderId !== 'me' ? true : msg.isRead
        }))
      }
    }));
  }
}));
